import { getDomainUserId } from 'analytics/snowplow/helpers/domainUserId'
import { setDynamicPricingMultiVariantUser, type DynamicPricingExperimentEntryStatus } from 'api/recommendations'
import { getLereDynamicPricingGroup, isEligibleForLereDynamicPricingAbTest } from 'checkout/lib/utils/lereDynamicPricing'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { LabFeatureFlags } from 'constants/lab'
import { getMemberIdForStitchedSeedLookup, resolveLereLabDistinctId } from 'lib/lab/labDistinctId'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { AppDispatch } from 'src/client/store'
import { canUseTrackingIdentityOnClient } from '../../consent/waitingForConsent'
import { getLereDynamicPricingExperimentStatus, getLereDomainOnlyExperimentGroup, getLereMemberEligibilityFlag, isLereMemberExcluded, hasConflictingLereExperimentVariants, hasRecordedLereExperimentEntry } from './lereDynamicPricingExperimentEntry'
import { isLereIdentityCurrent, registerLereExposureIdentity, storeLereDynamicPricingFlag, trackLereDynamicPricingExposure } from './lereDynamicPricingExposure'
import type { LereDynamicPricingGroup } from './types'

function canRestoreLereExperiment(state: App.State): boolean {
  const isLabEnabled = !!state.system.features.flags[OptimizelyFeatureFlags.enableLabFeatureFlag]
  return isLabEnabled && isEligibleForLereDynamicPricingAbTest() && canUseTrackingIdentityOnClient()
}

interface LereLoginAssignment {
  deviceGroup?: LereDynamicPricingGroup
  group?: LereDynamicPricingGroup
  isMultiVariant: boolean
}

function getLoginAssignment(status: DynamicPricingExperimentEntryStatus, existingGroup?: LereDynamicPricingGroup): LereLoginAssignment {
  // Redux is authoritative when Snowplow has not persisted the device's exposure yet.
  const deviceGroup = existingGroup ?? status.domain_triggered_variant ?? undefined
  const hasVariantConflict = hasConflictingLereExperimentVariants(status, deviceGroup)

  const isMultiVariant = status.is_multi_variant || hasVariantConflict
  const memberGroup = isMultiVariant ? undefined : status.triggered_variant ?? undefined
  return { deviceGroup, group: deviceGroup ?? memberGroup, isMultiVariant }
}

interface PersistLoginAssignmentOptions {
  isMultiVariant: boolean
  shouldMarkMultiVariant: boolean
  assignmentDistinctId?: string
}

async function persistLoginAssignment({
  isMultiVariant,
  shouldMarkMultiVariant,
  assignmentDistinctId,
}: PersistLoginAssignmentOptions): Promise<void> {
  // Multi-variant members retain device identities; they must never register a shared seed.
  if (isMultiVariant) {
    if (shouldMarkMultiVariant) await setDynamicPricingMultiVariantUser()
    return
  }

  if (assignmentDistinctId) await registerLereExposureIdentity(assignmentDistinctId)
}

interface RestoreLoginAssignmentOptions {
  shouldMarkMultiVariant: boolean
  assignment: LereLoginAssignment
  existingFlag?: App.LabFeatureFlagResult
  memberId?: string
  domainUserId?: string
  dispatch: AppDispatch
  getState: () => App.State
}

async function restoreLoginAssignment({
  shouldMarkMultiVariant,
  assignment,
  existingFlag,
  memberId,
  domainUserId,
  dispatch,
  getState,
}: RestoreLoginAssignmentOptions): Promise<void> {
  if (!assignment.group) return

  let assignmentDistinctId = existingFlag?.assignmentDistinctId
  if (!assignmentDistinctId) {
    assignmentDistinctId = assignment.deviceGroup ? domainUserId : await resolveLereLabDistinctId(getState())
  }
  const currentState = getState()
  if (!isLereIdentityCurrent(currentState, memberId)) return

  const isLoggedIn = selectLoggedIn(currentState)
  if (isLoggedIn) {
    await persistLoginAssignment({ isMultiVariant: assignment.isMultiVariant, shouldMarkMultiVariant, assignmentDistinctId })
  }

  // Identity, consent, or exclusion can change while persistence is pending.
  if (!isLereIdentityCurrent(getState(), memberId) || isLereMemberExcluded(getState().system.features.labFlags, memberId)) return
  storeLereDynamicPricingFlag(dispatch, {
    ...existingFlag,
    key: LabFeatureFlags.enableLereDynamicPricingGroup,
    enabled: true,
    variant: assignment.group,
    assignmentDistinctId,
    useDomainUserId: assignment.isMultiVariant,
  })
  if (assignmentDistinctId) {
    trackLereDynamicPricingExposure(assignment.group, assignmentDistinctId, getState())
  }
}

/** Return an identity for /decide only when a recorded entrant needs a fresh-page restoration. */
export async function restoreLereDynamicPricingForCurrentUser(
  dispatch: AppDispatch,
  getState: () => App.State,
  isLogin: boolean,
): Promise<string | undefined> {
  const state = getState()

  if (!canRestoreLereExperiment(state)) return undefined

  const memberId = getMemberIdForStitchedSeedLookup(state)
  const domainUserId = state.auth.domainUserId ?? getDomainUserId()
  const flagKey = LabFeatureFlags.enableLereDynamicPricingGroup
  const existingFlag = state.system.features.labFlags[flagKey]
  const existingGroup = getLereDynamicPricingGroup(state)
  if (isLereMemberExcluded(state.system.features.labFlags, memberId)) return undefined

  try {
    const status = await getLereDynamicPricingExperimentStatus(memberId, domainUserId)
    if (!status || !isLereIdentityCurrent(getState(), memberId)) return undefined

    const eligibilityFlag = getLereMemberEligibilityFlag(status, memberId, existingFlag)
    if (eligibilityFlag) storeLereDynamicPricingFlag(dispatch, eligibilityFlag)
    if (isLereMemberExcluded(getState().system.features.labFlags, memberId)) return undefined

    const assignment = getLoginAssignment(status, existingGroup)

    const hasDomainOnlyExposure = !!getLereDomainOnlyExperimentGroup(status)
    if ((isLogin || hasDomainOnlyExposure) && assignment.group) {
      // Reuse this assignment: /decide may still see the old mapping immediately after registration.
      await restoreLoginAssignment({
        assignment,
        shouldMarkMultiVariant: isLogin && !status.is_multi_variant,
        existingFlag: eligibilityFlag,
        memberId,
        domainUserId,
        dispatch,
        getState,
      })
      return undefined
    }
    if (existingGroup) return undefined

    // Multi-variant history can exist without a group in this page's Redux.
    // Set the identity policy before the next /decide, leaving the group unassigned.
    storeLereDynamicPricingFlag(dispatch, { ...eligibilityFlag, key: flagKey, enabled: false, useDomainUserId: assignment.isMultiVariant })
    if (!hasRecordedLereExperimentEntry(status)) return undefined

    const distinctId = await resolveLereLabDistinctId(getState())
    return isLereIdentityCurrent(getState(), memberId) ? distinctId : undefined
  } catch(error) {
    await reportClientError(error)
    if (isLogin && existingGroup && existingFlag?.assignmentDistinctId && isLereIdentityCurrent(getState(), memberId)) {
      trackLereDynamicPricingExposure(existingGroup, existingFlag.assignmentDistinctId, getState())
    }
    return undefined
  }
}
